import type { ElementInfo } from './types';
import { elementForIndex } from './extractor';
import { el, shellHost, shellRoot } from './shell';
import { textOf } from './dom';

let layer: HTMLDivElement | null = null;
let marks: HTMLElement[] = [];
let target: { node: HTMLElement; tip: HTMLElement; index: number } | null = null;
let trackRaf = 0;
let bound = false;

function ensureLayer(): HTMLDivElement | null {
  const root = shellRoot();
  if (!root) return null;
  if (!layer) {
    layer = el('div', 'overlay-layer');
    root.appendChild(layer);
  }
  return layer;
}

function bindDismissers(): void {
  if (bound) return;
  bound = true;
  window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  document.addEventListener('pointerdown', onHostInteract, { passive: true, capture: true });
}

function onScrollOrResize(): void {
  if (marks.length > 0) clearMarks();
}

/**
 * Once the user reaches for the page themselves the overlay has either done its
 * job or is in the way, so it goes. Our own panel is exempt: reading an approval
 * should not put out the light on the thing it is asking about. Listening in the
 * capture phase means a host that swallows events cannot strand the overlay, but
 * it also runs before the shell contains its own input, so the check is explicit.
 */
function onHostInteract(event: Event): void {
  const own = shellHost();
  const target = event.target;
  if (own && target instanceof Node && (own === target || own.contains(target))) return;
  clear();
}

export function markAll(elements: readonly ElementInfo[]): void {
  const host = ensureLayer();
  if (!host) return;
  clearMarks();
  bindDismissers();

  for (const info of elements) {
    const node = elementForIndex(info.index);
    if (!node) continue;
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) continue;
    if (rect.bottom < 0 || rect.top > window.innerHeight) continue;

    const ring = el('div', 'mark__ring');
    ring.style.left = `${rect.left}px`;
    ring.style.top = `${rect.top}px`;
    ring.style.width = `${rect.width}px`;
    ring.style.height = `${rect.height}px`;

    const badge = el('div', 'mark', String(info.index));
    badge.style.left = `${Math.max(0, rect.left - 2)}px`;
    badge.style.top = `${Math.max(0, rect.top - 9)}px`;

    host.append(ring, badge);
    marks.push(ring, badge);
  }
}

function clearMarks(): void {
  for (const node of marks) node.remove();
  marks = [];
}

const TIP_MIN = 200;
const TIP_MAX = 640;
const TIP_GAP = 12;

function reservedByPanel(): number {
  const panel = shellRoot()?.querySelector('.panel');
  if (!(panel instanceof HTMLElement) || panel.dataset.open !== 'true') return 0;
  return panel.getBoundingClientRect().width;
}

function sizeTip(box: HTMLElement, tip: HTMLElement, rect: DOMRect, pad: number): void {
  const rightLimit = window.innerWidth - reservedByPanel() - TIP_GAP;
  const fromLeft = rightLimit - (rect.left - pad);
  const alignRight = fromLeft < TIP_MIN;
  const available = alignRight ? rect.right + pad - TIP_GAP : fromLeft;
  const width = Math.round(Math.min(TIP_MAX, Math.max(TIP_MIN, available)));

  const align = alignRight ? 'right' : 'left';
  if (box.dataset.align !== align) box.dataset.align = align;
  const next = `${width}px`;
  if (tip.style.maxWidth !== next) tip.style.maxWidth = next;
}

function trackTarget(): void {
  if (!target) return;
  const node = elementForIndex(target.index);
  if (!node) {
    clearTarget();
    return;
  }
  const rect = node.getBoundingClientRect();
  const pad = 4;
  target.node.style.left = `${rect.left - pad}px`;
  target.node.style.top = `${rect.top - pad}px`;
  target.node.style.width = `${rect.width + pad * 2}px`;
  target.node.style.height = `${rect.height + pad * 2}px`;
  target.node.dataset.flip = rect.top < 48 ? 'true' : 'false';
  sizeTip(target.node, target.tip, rect, pad);
  trackRaf = requestAnimationFrame(trackTarget);
}

export function highlightTarget(index: number): string {
  const host = ensureLayer();
  const node = elementForIndex(index);
  if (!host || !node) return `element ${index} is not on the page`;

  clearTarget();
  bindDismissers();
  node.scrollIntoView({ block: 'center', behavior: 'smooth' });

  const box = el('div', 'target');
  const tip = el('div', 'target__tip', textOf(node) || `Element ${index}`);
  box.appendChild(tip);
  host.appendChild(box);

  target = { node: box, tip, index };
  trackTarget();

  return `Highlighted ${textOf(node) || `element ${index}`}.`;
}

function clearTarget(): void {
  if (trackRaf) cancelAnimationFrame(trackRaf);
  trackRaf = 0;
  target?.node.remove();
  target = null;
}

export function clear(): void {
  clearMarks();
  clearTarget();
}

export function destroyHighlighter(): void {
  clear();
  window.removeEventListener('scroll', onScrollOrResize, { capture: true });
  window.removeEventListener('resize', onScrollOrResize);
  document.removeEventListener('pointerdown', onHostInteract, { capture: true });
  bound = false;
  layer?.remove();
  layer = null;
}
